import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";

import {
  HexButton,
  HexMenu,
  HexMenuItem,
  HexPopover,
  HexTooltip,
} from "../../../hex-components/index.js";
import { ORG_ID } from "../../../orgs.js";
import { Routes } from "../../../route/routes.js";
import { usePinData } from "../../data/schema-browser-v2/usePinData.js";
import { getSchemaIdPath } from "../../data/schema-browser-v2/utils.js";
import {
  FolderIcon,
  MoreMenuIcon,
  StarActiveIcon,
} from "../../icons/CustomIcons.js";
import { PinnedSchemaFragment } from "../../sidebar/data-browser/FavoritesView.generated.js";

import { FavoriteDataMenuItem } from "./FavoriteDataTableRow.js";
import { FixedHeightMenuItem } from "./styled.js";
import { isModifiedEvent } from "./utils.js";

interface FavoriteDataSchemaRowProps {
  schema: PinnedSchemaFragment;
  multiDatabase: boolean;
}

const FavoriteDataSchemaMenuButton: React.ComponentType<FavoriteDataSchemaRowProps> =
  React.memo(function FavoriteDataSchemaMenuButton({
    multiDatabase,
    schema,
  }: FavoriteDataSchemaRowProps) {
    const pinSchema = usePinData();

    const onClickRemoveFavorite = useCallback(async () => {
      await pinSchema({
        id: schema.id,
        dataConnectionId: schema.dataConnectionId,
        idPath: getSchemaIdPath(schema, multiDatabase),
        multiDatabase,
        kind: "schema",
        newPinnedState: false,
      });
    }, [pinSchema, schema, multiDatabase]);

    const { userActionColor } = useTheme();

    const [isOpen, setIsOpen] = useState(false);

    const onPopoverInteraction = useCallback(
      (newIsOpen: boolean, e?: React.SyntheticEvent) => {
        setIsOpen(newIsOpen);
        // prevent event from bubbling up to the parent and
        // triggering data bento row click
        e?.stopPropagation();
      },
      [setIsOpen],
    );

    return (
      <HexPopover
        captureDismiss={true}
        content={
          <HexMenu>
            <HexMenuItem
              icon={<StarActiveIcon color={userActionColor} />}
              text="Remove favorite"
              onClick={onClickRemoveFavorite}
            />
          </HexMenu>
        }
        isOpen={isOpen}
        minimal={true}
        position="bottom-left"
        onInteraction={onPopoverInteraction}
      >
        <HexButton icon={<MoreMenuIcon />} minimal={true} small={true} />
      </HexPopover>
    );
  });

export const FavoriteDataSchemaRow: React.FunctionComponent<FavoriteDataSchemaRowProps> =
  React.memo(function FavoriteDataSchemaRow({ multiDatabase, schema }) {
    const navigate = useNavigate();

    const onClick = useCallback(
      (evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        const withModifier = isModifiedEvent(evt);
        if (withModifier) {
          const url = Routes.DATA_BROWSER.href(ORG_ID, true, {
            tab: "all",
            detailViewType: "schema",
            dataSourceId: schema.id,
          });
          window.open(url, "_blank");
          return;
        }
        Routes.DATA_BROWSER.push(navigate, {
          tab: "all",
          detailViewType: "schema",
          dataSourceId: schema.id,
        });
      },
      [navigate, schema.id],
    );

    return (
      <HexTooltip content={schema.name} targetTagName="div">
        <FixedHeightMenuItem
          icon={<FolderIcon />}
          text={
            <FavoriteDataMenuItem
              hoverContent={
                <FavoriteDataSchemaMenuButton
                  multiDatabase={multiDatabase}
                  schema={schema}
                />
              }
              id={`${schema.id}-sidebar-hover-container`}
              text={schema.name}
            />
          }
          onClick={onClick}
        />
      </HexTooltip>
    );
  });
